import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { UsersService, User, GetMeResponse, Organization, OrganizationGroup } from '../users/users.service';
import { AuthEventsService, AuthEventType } from '../auth/auth-events.service';
import { OrganizationGroupService } from '../organization-group/organization-group.service';

export enum UserRole {
  SUPER_ADMIN = 'SUPER_ADMIN',
  SUPERVISOR = 'SUPERVISOR',
  MANAGER = 'MANAGER',
  ADVISOR = 'ADVISOR'
}

@Injectable({
  providedIn: 'root'
})
export class UserRoleService {
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  private userRoleSubject = new BehaviorSubject<UserRole | null>(null);
  public userRole$ = this.userRoleSubject.asObservable();

  constructor(
    private usersService: UsersService,
    private authEventsService: AuthEventsService,
    private organizationGroupService: OrganizationGroupService,
  ) {
    // Listen for auth events to update user data
    this.authEventsService.authEvents$.subscribe(event => {
      switch (event.type) {
        case AuthEventType.LOGGED_IN:
          this.loadCurrentUser();
          break;
        case AuthEventType.LOGGED_OUT:
        case AuthEventType.SESSION_EXPIRED:
          this.clearUserData();
          break;
      }
    });

    // Initial load of current user will be triggered by AuthService.initializeAuthStatus(),
    // AuthService.login(), or the LOGGED_IN event.
    // Removing direct call to this.loadCurrentUser() from constructor.
  }

  async loadCurrentUser(): Promise<void> {
    try {
      const userData = await this.usersService.getCurrentUser();
      this.setCurrentUser(userData.USER);

      // Seed active organization/group for super admin/supervisor firm switching
      this.seedActiveContext(userData);

      // Emit event that user data has been loaded
      this.authEventsService.emitEvent({
        type: AuthEventType.USER_DATA_LOADED,
        payload: userData.USER
      });
    } catch (error) {
      console.error('Failed to load current user:', error);
      this.clearUserData();
    }
  }

  /**
   * Seed active organization and group context for firm switching.
   * This ensures the firm picker modal has initial values when opened.
   */
  private seedActiveContext(userData: GetMeResponse): void {
    const org: Organization | null = userData.ORGANIZATION ?? null;
    const group: OrganizationGroup | null = userData.ORGANIZATION_GROUP ?? null;

    if (org) {
      this.organizationGroupService.seedActiveOrgFromOrganization(org);
    }

    if (group) {
      this.organizationGroupService.seedActiveGroup(group);
    }

    // If we have an org but no group, try to construct a minimal group from org.GROUP_ID
    // This handles the case where backend doesn't return ORGANIZATION_GROUP but org has GROUP_ID
    if (org && !group && org.GROUP_ID != null) {
      this.organizationGroupService.seedActiveGroup({
        GROUP_ID: org.GROUP_ID,
        GROUP_NAME: org.ORGANIZATION_NAME ?? 'Unknown Group',
      });
    }
  }

  private setCurrentUser(user: User): void {
    this.currentUserSubject.next(user);
    this.userRoleSubject.next(user.ROLE as UserRole);

    // Store in session for persistence
    sessionStorage.setItem('currentUser', JSON.stringify(user));
  }

  private clearUserData(): void {
    this.currentUserSubject.next(null);
    this.userRoleSubject.next(null);
    sessionStorage.removeItem('currentUser');
  }

  public getCurrentRole(): UserRole | null {
    return this.userRoleSubject.value;
  }

  public hasRole(role: UserRole | UserRole[]): boolean {
    const currentRole = this.getCurrentRole();
    if (!currentRole) return false;

    if (Array.isArray(role)) {
      return role.includes(currentRole);
    }

    return currentRole === role;
  }

  // TODO(capabilities): replace role check with capability flag from /me/capabilities
  //   (e.g. capabilities.canViewAuditTrail). Keep role check until UI-wide capability
  //   service is introduced.
  public isSuperAdmin(): boolean {
    return this.getCurrentRole() === UserRole.SUPER_ADMIN;
  }

  // TODO(capabilities): replace role check with capability flag from /me/capabilities
  //   (e.g. capabilities.canViewAuditTrail). Keep role check until UI-wide capability
  //   service is introduced.
  public isSupervisor(): boolean {
    return this.getCurrentRole() === UserRole.SUPERVISOR;
  }

  /**
   * @deprecated Use {@link isSupervisor} — backend role `ADMIN` renamed to `SUPERVISOR`.
   */
  public isAdmin(): boolean {
    return this.isSupervisor();
  }

  // TODO(capabilities): replace role check with capability flag from /me/capabilities
  //   (e.g. capabilities.canViewAuditTrail). Keep role check until UI-wide capability
  //   service is introduced.
  public isManager(): boolean {
    return this.getCurrentRole() === UserRole.MANAGER;
  }

  // TODO(capabilities): replace role check with capability flag from /me/capabilities
  //   (e.g. capabilities.canViewAuditTrail). Keep role check until UI-wide capability
  //   service is introduced.
  public isAdvisor(): boolean {
    return this.getCurrentRole() === UserRole.ADVISOR;
  }

  // TODO(capabilities): replace role check with capability flag from /me/capabilities
  //   (e.g. capabilities.canViewAuditTrail). Keep role check until UI-wide capability
  //   service is introduced.
  public isManagerOrSupervisor(): boolean {
    const role = this.getCurrentRole();
    return role === UserRole.MANAGER || role === UserRole.SUPERVISOR;
  }

  /**
   * @deprecated Use {@link isManagerOrSupervisor}.
   */
  public isManagerOrAdmin(): boolean {
    return this.isManagerOrSupervisor();
  }

  // TODO(capabilities): replace role check with capability flag from /me/capabilities
  //   (e.g. capabilities.canViewAuditTrail). Keep role check until UI-wide capability
  //   service is introduced.
  public isSupervisorOrSuperAdmin(): boolean {
    const role = this.getCurrentRole();
    return role === UserRole.SUPERVISOR || role === UserRole.SUPER_ADMIN;
  }

  /**
   * @deprecated Use {@link isSupervisorOrSuperAdmin}.
   */
  public isAdminOrSuperAdmin(): boolean {
    return this.isSupervisorOrSuperAdmin();
  }

  // TODO(capabilities): replace role check with capability flag from /me/capabilities
  //   (e.g. capabilities.canViewAuditTrail). Keep role check until UI-wide capability
  //   service is introduced.
  public isManagerOrHigher(): boolean {
    const role = this.getCurrentRole();
    return (
      role === UserRole.MANAGER ||
      role === UserRole.SUPERVISOR ||
      role === UserRole.SUPER_ADMIN
    );
  }

  // TODO(capabilities): replace role check with capability flag from /me/capabilities
  //   (e.g. capabilities.canViewAuditTrail). Keep role check until UI-wide capability
  //   service is introduced.
  public isSupervisorOrHigher(): boolean {
    return this.isSupervisorOrSuperAdmin();
  }
}
